import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import logger from "@/lib/logger";
import { fetchCampaign } from "@/src/api/campaigns";
import { LoadingStep } from "@/src/components/wizard/loading-step";
import { ScenarioStep } from "@/src/components/wizard/scenario-step";

// Scenario is the ONLY setup mode now (spec §7) — every campaign is born
// with its PC via the Launch hub's character-backed launch flow, so this
// page never runs character creation. `?mode=scenario` on the incoming
// route is accepted-but-unused: it keeps the Launch hub's existing
// `/campaigns/:id/setup?mode=scenario` navigations valid without this page
// needing to special-case its presence or absence.
type SetupStep = "scenario" | "loading";

export default function CampaignSetupPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<SetupStep>("scenario");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [validated, setValidated] = useState(false);

  // Validate campaign exists (and is not archived) on mount.
  useEffect(() => {
    if (!campaignId) {
      navigate("/launch");
      return;
    }
    fetchCampaign(campaignId)
      .then((campaign) => {
        if (campaign) {
          setValidated(true);
        } else {
          navigate("/launch");
        }
      })
      .catch((err) => {
        if (err instanceof Error && err.message === "campaign_archived") {
          navigate("/launch");
          return;
        }
        logger.error({ err }, "Failed to validate campaign");
        navigate("/launch");
      });
  }, [campaignId, navigate]);

  const handleComplete = useCallback(() => {
    navigate(`/campaigns/${campaignId}`);
  }, [campaignId, navigate]);

  if (!campaignId || !validated) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background text-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground">
      {step === "scenario" && (
        <ScenarioStep
          onBack={() => navigate(`/campaigns/${campaignId}`)}
          onNext={(id) => {
            setTemplateId(id);
            setStep("loading");
          }}
          onSkip={() => {
            setTemplateId(null);
            setStep("loading");
          }}
          selectedTemplateId={templateId}
        />
      )}
      {step === "loading" && (
        <LoadingStep
          campaignId={campaignId}
          draft={null}
          expectedPlanDigest={null}
          onBack={() => setStep("scenario")}
          onComplete={handleComplete}
          templateId={templateId}
        />
      )}
    </div>
  );
}
